import { cn } from '@invent/wl-ui-kit-next';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@invent/wl-ui-kit-next/ui';
import { Palette } from 'lucide-react';

import type { IListFormattingRule } from '../../api/lists-contracts';
import { describeLeaf, leafOf, type ICriteriaWords } from '../../list-criteria';
import { formattingTone, formattingToneLabel } from '../../lists';
import { formattingClass } from './cells';

/**
 * The footer's Legend (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.8) — Conga's, for Conga's reason: a grid that colours its cells has to say what each colour
 * means where the analyst looks for it, or the colour is a private code. It names every formatting
 * rule of the view in words — "Travel is not blank → blue", bold and whole-row said — a migrated
 * colour no tone names as "colour not carried over (#CCE5FF)", and the two marks the grid draws of
 * its own: a cell edited and not saved, and a row a save took out of the list.
 */
export function Legend({
  rules,
  words
}: {
  rules: readonly IListFormattingRule[];
  words: ICriteriaWords;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" className="h-7 gap-1.5 px-2 font-normal">
          <Palette className="size-4" aria-hidden />
          Legend
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label="Legend" className="grid w-96 gap-2 p-3 text-[0.8125rem]">
        <p className="text-xs font-medium text-[var(--veritant-royal-70)]">This view’s colours</p>
        {rules.length === 0 ? (
          <p className="text-muted-foreground">This view colours nothing.</p>
        ) : (
          <ul className="grid gap-1.5">
            {rules.map((rule, index) => {
              const look = { tone: formattingTone(rule.tone).tone, bold: rule.bold === true };
              const condition =
                rule.field && rule.op
                  ? describeLeaf(leafOf(rule.field, rule.op, rule.value), words)
                  : 'A rule with no condition — it colours nothing';
              return (
                <li key={index} data-legend-rule={index} className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className={cn('mt-0.5 h-4 w-6 shrink-0 rounded-sm border border-border', formattingClass(look))}
                  />
                  <span>
                    {condition} → {formattingToneLabel(rule.tone)}
                    {rule.bold && ', bold'}
                    {rule.row && ', the whole row'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-1 text-xs font-medium text-[var(--veritant-royal-70)]">Cells and rows</p>
        <ul className="grid gap-1.5">
          <li className="flex items-center gap-2">
            <span className="border-l-[3px] border-[var(--color-yellow)] pl-1.5">Edited</span>
            <span className="text-muted-foreground">changed, not saved — Save data saves it</span>
          </li>
          <li className="flex items-center gap-2">
            <s className="text-[var(--veritant-royal-70)] opacity-70">Struck</s>
            <span className="text-muted-foreground">no longer matches this list — gone on Refresh</span>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}
